import React, { useState } from 'react';
import classnames from 'classnames';
import styles from './detailsTab.module.scss';
import { Spinner } from 'react-bootstrap';
import {
  DataVendor,
  IamACustomerDRMEntityType,
  IamACustomerDrmEntity,
} from 'v2/models';
import SectionHeader from 'v2/common/DRM/SectionHeader/SectionHeader';
import EditOwnerModal from 'v2/common/DataVendorModals/EditOwnerModal/EditOwnerModal';
import useGetStrapiUser from 'v2/hooks/useGetOwner';
import CurrentlyPurchasedField from 'v2/common/DRM/DataProduct/CurrentlyPurchasedField/CurrentlyPurchasedField';
import HangOnASecondModal, {
  useIamACustomerMessage,
} from 'v2/common/DRM/HangOnASecondModal/HangOnASecondModal';
import restFactory from 'v2/utils/restFactory';
import { DataProductQueryResult } from '../DataProductsTab/DataProductsContainer';
import { TranslationKeys, PostProcessKeys } from 'translations';
import { useTranslation } from 'react-i18next';

interface DetailsTabProps {
  dataVendor: DataVendor;
  onChange: () => void;
}

function NoDataText({ text }: { text: string }) {
  const { t } = useTranslation();

  return (
    <label
      data-testid={`noData_${text.replaceAll(' ', '')}`}
      className={styles.noData}
    >{`${text} for this ${t(TranslationKeys.dataProvider_one)}.`}</label>
  );
}

const DetailsTab = ({ dataVendor, onChange }: DetailsTabProps) => {
  const { t } = useTranslation();
  const iamACustomerMessage = useIamACustomerMessage();
  const canEdit = dataVendor.is_editable;

  const dataVendorEntity: IamACustomerDRMEntityType = {
    iAmACustomer: dataVendor.am_a_customer,
    id: dataVendor.id,
    type: IamACustomerDrmEntity.DATA_VENDOR,
  };

  const [showEditOwnerModal, setShowEditOwnerModal] = useState(false);
  const [showHangOnASecondModal, setShowHangOnASecondModal] = useState(false);

  const { data: ownerData, isFetching: ownerIsFetching } = useGetStrapiUser(
    dataVendor.owner
  );

  if (ownerIsFetching) {
    return (
      <div className={classnames(styles.loading)}>
        <Spinner animation="border" />
      </div>
    );
  }

  const handleEditClose = () => {
    setShowEditOwnerModal(false);
    onChange();
  };

  const handleUpdateCurrentlyPurchasedField = async (isACustomer: boolean) => {
    if (!isACustomer) {
      const purchasedDatasets = await restFactory.get<DataProductQueryResult>(
        `/dj/api/v1/data_vendor/${dataVendor.id}/data_products/`,
        { currently_purchased: true }
      );
      if (purchasedDatasets?.count > 0) {
        setShowHangOnASecondModal(true);
      }
    }
    onChange();
  };

  const handleCloseHangOnASecondModal = () => {
    setShowHangOnASecondModal(false);
  };

  const markDataProductsAsNotPurchased = async () => {
    await restFactory.patch(
      `/dj/api/v1/data_vendor/${dataVendor.id}/mark_data_products_as_not_purchased/`
    );
    setShowHangOnASecondModal(false);
  };

  return (
    <>
      <div>
        <CurrentlyPurchasedField
          drmEntity={dataVendorEntity}
          canEdit={canEdit}
          onChange={handleUpdateCurrentlyPurchasedField}
        />
        <div
          className={styles.field}
          data-cy="data-vendor-details:data-provider-owner-container"
        >
          <SectionHeader
            title={`${t(TranslationKeys.dataProvider_one, {
              postProcess: PostProcessKeys.TitleCase,
            })} Owner`}
            isEditable={dataVendor.is_editable}
            className="mb-3"
            onClick={() => setShowEditOwnerModal(true)}
          />
          {ownerData ? (
            <div
              data-testid={`value_Owner`}
              className={styles.descriptionTexts}
            >
              <div>
                <div className={classnames(styles.ownerHeader)}>
                  {ownerData?.first_name} {ownerData?.last_name}
                </div>
                <div className={classnames(styles.ownerDescription)}>
                  {ownerData?.job_title && <p>{ownerData?.job_title}</p>}
                  {ownerData?.email && (
                    <p>
                      <a href={`mailto:${ownerData?.email}`}>
                        {ownerData?.email}
                      </a>
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <NoDataText
              text={`Enter a ${t(TranslationKeys.dataProvider_one)} owner`}
            />
          )}
        </div>
      </div>
      <EditOwnerModal
        show={showEditOwnerModal}
        dataVendor={dataVendor}
        owner={ownerData}
        onClose={handleEditClose}
      />
      {showHangOnASecondModal && (
        <HangOnASecondModal
          onCancel={handleCloseHangOnASecondModal}
          onOk={() => markDataProductsAsNotPurchased()}
          {...iamACustomerMessage.NotCustomerOfDataVendorButCustomerOfAnyDataset}
        />
      )}
    </>
  );
};

export default DetailsTab;
